import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMealPhotoCapture, getAnglePrompt } from '../hooks/useMealPhotoCapture';
import SwitchCameraIcon from './icons/SwitchCameraIcon';
import { verifyQuestVisually } from '../api/dataService';
import { useLocale } from '../context/LocaleContext';

const angleCopy = {
  top: 'Top',
  front: 'Front',
  side: 'Side'
};

const initialFeedback = { type: '', message: '' };

function MealCaptureModal({ isOpen, quest, onClose, onSuccess, onQuotaStateChange }) {
  const { t } = useLocale();
  const videoRef = useRef(null);
  const {
    stream,
    facingMode,
    isInitializing,
    isCameraUnavailable,
    error,
    currentAngle,
    angleOrder,
    captures,
    hasAllCaptures,
    startCamera,
    stopCamera,
    toggleCamera,
    captureCurrentAngle,
    retakeAngle,
    reset,
    setError
  } = useMealPhotoCapture();

  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(initialFeedback);

  const currentPrompt = useMemo(() => {
    if (!currentAngle) return t('mealCapture.allAnglesCaptured', 'All required angles captured.');
    const rawPrompt = getAnglePrompt(currentAngle);
    const promptKey = `mealCapture.anglePrompts.${currentAngle}`;
    return t(promptKey, rawPrompt);
  }, [currentAngle, t]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let mounted = true;
    (async () => {
      const streamInstance = await startCamera();
      if (!mounted) {
        return;
      }
      if (videoRef.current && streamInstance) {
        const videoEl = videoRef.current;
        videoEl.srcObject = streamInstance;
        try {
          await videoEl.play();
        } catch (err) {
          console.warn('[MealCaptureModal] Failed to autoplay preview video', err);
        }
      }
    })();

    return () => {
      mounted = false;
      stopCamera();
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      reset();
      setDescription('');
      setFeedback(initialFeedback);
    };
  }, [isOpen, reset, startCamera, stopCamera]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !stream) return;
    if (videoEl.srcObject === stream) return;
    videoEl.srcObject = stream;
    videoEl
      .play()
      .catch((err) => console.warn('[MealCaptureModal] Autoplay failed after stream update', err));
  }, [stream]);

  const handleCapture = async () => {
    if (!videoRef.current) {
      setError(t('mealCapture.cameraPreviewNotReady', 'Camera preview is not ready.'));
      return;
    }
    const result = await captureCurrentAngle(videoRef.current);
    if (result) {
      setFeedback({
        type: 'success',
        message: t('mealCapture.angleCaptured', '{angle} angle captured.', {
          angle: angleCopy[currentAngle]
        })
      });
    }
  };

  const handleRetake = (angle) => {
    retakeAngle(angle);
    setFeedback(initialFeedback);
  };

  const handleClose = () => {
    const hasAnyCapture = angleOrder.some((angle) => Boolean(captures[angle]));
    if (hasAnyCapture) {
      const confirmExit = window.confirm(t('mealCapture.discardConfirm', 'Discard captured photos and exit?'));
      if (!confirmExit) return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (!hasAllCaptures) {
      setFeedback({ type: 'error', message: t('mealCapture.captureAllAngles', 'Capture all three angles before submitting.') });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: 'info', message: t('mealCapture.submitting', 'Submitting photos for verification...') });

    try {
      const response = await verifyQuestVisually(quest._id, description, {
        angles: {
          top: captures.top,
          front: captures.front,
          side: captures.side
        }
      });

      onQuotaStateChange?.();
      setFeedback({ type: 'success', message: response.data.message });
      onSuccess(response.data);
    } catch (err) {
      const status = err.response?.status;
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        t('mealCapture.failed', 'Meal verification failed. Please try again.');
      onQuotaStateChange?.();
      setFeedback({ type: status === 403 || status === 429 ? 'info' : 'error', message });
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const facingLabel = facingMode === 'environment'
    ? t('mealCapture.rearCamera', 'Rear')
    : t('mealCapture.frontCamera', 'Front');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6">
      <div className="relative flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-cyan-400/30 bg-[#1b1d21] shadow-2xl">
        <header className="flex items-center justify-between border-b border-white/5 bg-[#23262b] px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="rounded-lg border border-white/10 px-3 py-1 text-sm font-medium text-gray-300 transition hover:bg-white/5"
            >
              {t('mealCapture.back', 'Back')}
            </button>
            <div>
              <h2 className="text-xl font-semibold text-white">{t('mealCapture.title', 'Meal Verification')}</h2>
              <p className="text-sm text-gray-400">{quest?.name}</p>
            </div>
          </div>
          <div className="text-sm text-gray-300">{t('mealCapture.deviceCamera', 'Device: {camera} camera', { camera: facingLabel })}</div>
        </header>

        <main className="grid flex-1 gap-6 overflow-y-auto px-6 py-6 lg:grid-cols-2">
          <section className="flex flex-col gap-4">
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black">
              {stream && !isCameraUnavailable ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center text-gray-400">
                  {isInitializing ? (
                    <span>{t('mealCapture.initializingCamera', 'Initializing camera...')}</span>
                  ) : (
                    <span>
                      {t('mealCapture.cameraRequired', 'Camera access is required for meal verification. Please switch to a device with a camera.')}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-white/10 bg-[#23262b] p-4">
              <p className="text-sm font-semibold uppercase tracking-wide text-cyan-300">
                {t('mealCapture.captureInstruction', 'Capture Instruction')}
              </p>
              <p className="mt-2 text-base text-gray-200">{currentPrompt}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCapture}
                disabled={!stream || isInitializing || isCameraUnavailable || !currentAngle || isSubmitting}
                className="flex flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 py-3 text-base font-semibold text-black shadow-lg shadow-yellow-500/20 transition hover:from-yellow-400 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t('mealCapture.captureButton', 'Capture {angle}', { angle: currentAngle ? angleCopy[currentAngle] : '' }).trim()}
              </button>
              <button
                onClick={toggleCamera}
                disabled={isInitializing || isSubmitting || isCameraUnavailable}
                className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <SwitchCameraIcon className="h-5 w-5 text-yellow-300" />
                {t('mealCapture.switch', 'Switch')}
              </button>
            </div>
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}
            {feedback.message && (
              <div
                className={`rounded-lg px-4 py-3 text-sm ${
                  feedback.type === 'success'
                    ? 'border border-emerald-400/40 bg-emerald-500/10 text-emerald-300'
                    : feedback.type === 'error'
                    ? 'border border-red-500/40 bg-red-500/10 text-red-300'
                    : 'border border-cyan-300/30 bg-cyan-500/10 text-cyan-200'
                }`}
              >
                {feedback.message}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-5">
            <div className="rounded-2xl border border-white/10 bg-[#23262b] p-4">
              <h3 className="text-lg font-semibold text-white">{t('mealCapture.captureProgress', 'Capture Progress')}</h3>
              <div className="mt-4 space-y-3">
                {angleOrder.map((angle) => {
                  const captured = Boolean(captures[angle]);
                  const isCurrent = currentAngle === angle;
                  return (
                    <div
                      key={angle}
                      className={`flex items-center justify-between rounded-xl border px-3 py-3 ${
                        captured
                          ? 'border-emerald-500/40 bg-emerald-500/10'
                          : isCurrent
                          ? 'border-cyan-300/40 bg-cyan-500/10'
                          : 'border-white/10 bg-white/5'
                      }`}
                    >
                      <div>
                        <p className="text-sm font-semibold text-white">{t('mealCapture.angleLabel', '{angle} angle', { angle: angleCopy[angle] })}</p>
                        <p className="text-xs text-gray-400">
                          {captured
                            ? t('mealCapture.captured', 'Captured')
                            : isCurrent
                              ? t('mealCapture.pending', 'Pending')
                              : t('mealCapture.awaitingTurn', 'Awaiting turn')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {captured && (
                          <button
                            onClick={() => handleRetake(angle)}
                            className="text-xs font-semibold text-yellow-300 transition hover:text-yellow-200"
                          >
                            {t('mealCapture.retake', 'Retake')}
                          </button>
                        )}
                        <span
                          className={`h-3 w-3 rounded-full ${
                            captured
                              ? 'bg-emerald-400'
                              : isCurrent
                              ? 'bg-cyan-400 animate-pulse'
                              : 'bg-gray-600'
                          }`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {angleOrder.map((angle) => (
                <div
                  key={angle}
                  className="relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-[#1b1d21]"
                >
                  {captures[angle] ? (
                    <img src={captures[angle]} alt={`${angle} angle preview`} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center px-4 text-center text-sm text-gray-500">
                      {t('mealCapture.anglePreview', '{angle} preview', { angle: angleCopy[angle] })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300">{t('mealCapture.descriptionOptional', 'Description (optional)')}</label>
              <textarea
                className="mt-2 h-32 w-full rounded-xl border border-white/10 bg-[#1b1d21] px-4 py-3 text-sm text-gray-200 outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/40"
                placeholder={t('mealCapture.descriptionPlaceholder', 'Add any details you want to share...')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="mt-auto flex items-center justify-between gap-3">
              <button
                onClick={handleClose}
                className="rounded-xl border border-white/10 px-4 py-3 text-sm font-semibold text-gray-300 transition hover:bg-white/5"
                disabled={isSubmitting}
              >
                {t('mealCapture.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !hasAllCaptures}
                className="flex items-center justify-center rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/20 transition hover:from-cyan-400 hover:to-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? t('mealCapture.submittingShort', 'Submitting...') : t('mealCapture.submitPhotos', 'Submit photos')}
              </button>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

MealCaptureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  quest: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onQuotaStateChange: PropTypes.func
};

export default MealCaptureModal;
